import { FiCheckCircle } from 'react-icons/fi';

export const multipleChoiceAdvancedData = {
  courseId: 'multiple-choice-advanced',
  courseType: 'reading-course',
  title: 'Multiple Choice Advanced',
  icon: FiCheckCircle,
  overview: {
    impact: 'Handle inference and close distractors with more confidence',
    focus: 'Evidence testing, option elimination, and careful scope checking',
    uses: 'Advanced reading comprehension',
    duration: '25-30 minutes',
  },
  sections: [
    {
      title: 'Why Multiple Choice Is Hard',
      content:
        '<p>In advanced IELTS multiple choice, all options may sound reasonable. Usually the wrong options fail because they are <strong>too broad</strong>, <strong>too strong</strong>, or based on a detail from the wrong part of the passage.</p>',
      points: [
        'One option usually matches the passage best, not perfectly word for word.',
        'One distractor often contains correct vocabulary but the wrong meaning.',
        'Inference questions reward evidence-based interpretation, not guessing.',
      ],
    },
    {
      title: 'An Elimination Strategy',
      content: '<p>Work through the options systematically:</p>',
      points: [
        'Read the question stem carefully before the options.',
        'Check each option against the passage and ask: supported, contradicted, or unproven?',
        'Eliminate clearly wrong answers first, then compare the two best options.',
        'Prefer the option that matches the author\'s exact level of certainty.',
      ],
      warning:
        'Do not choose an option just because it sounds generally true in real life.',
    },
    {
      title: 'Common Distractor Types',
      content: '<p>Wrong options usually follow recurring patterns:</p>',
      table: {
        headers: ['Distractor', 'What It Looks Like'],
        rows: [
          ['Too strong', 'always, proves, completely, all'],
          ['Wrong focus', 'Uses the right topic but answers a different question'],
          ['Partial truth', 'Matches one detail but not the whole claim'],
          ['Outside knowledge', 'Sounds plausible but is not stated in the passage'],
          ['Wrong speaker', 'Belongs to an expert quoted in the text, not the author'],
        ],
      },
    },
    {
      title: 'Inference vs Direct Fact',
      content:
        '<p>Some questions ask for what is directly stated; others ask what can reasonably be concluded. In both cases, your answer must still come from the passage.</p>',
      examples: [
        {
          label: 'Direct fact',
          text: 'The passage says sales rose by 12%, so an option about higher sales is directly supported.',
        },
        {
          label: 'Inference',
          text: 'If the passage says customers returned less often after prices increased, you may infer dissatisfaction, but only if the evidence clearly points that way.',
        },
      ],
    },
  ],
  exercises: [
    {
      title: 'Eliminate the Distractor',
      type: 'select',
      instructions: 'Choose the best answer based on the passage.',
      questions: [
        {
          text:
            'Passage: "Although the policy reduced commuting times slightly, most workers said the biggest benefit was the increase in flexible working options."\nQuestion: What was the main advantage of the policy?',
          options: [
            'It made commuting much faster for everyone.',
            'It gave workers greater flexibility.',
            'It removed the need to travel to work at all.',
          ],
          answer: 'It gave workers greater flexibility.',
          explanation:
            'The passage says the biggest benefit was increased flexible working options.',
        },
        {
          text:
            'Passage: "The scientist warned that early results were promising but far from conclusive."\nQuestion: How does the scientist view the results?',
          options: [
            'They are definitely correct.',
            'They are encouraging but still uncertain.',
            'They should be ignored completely.',
          ],
          answer: 'They are encouraging but still uncertain.',
          explanation:
            '"Promising but far from conclusive" shows cautious optimism, not certainty.',
        },
      ],
    },
    {
      title: 'Why the Wrong Option Fails',
      type: 'select',
      instructions: 'Choose the best label for the distractor.',
      questions: [
        {
          text:
            'Passage: "Some towns introduced bike lanes, but accident rates changed little."\nWrong option: "Bike lanes eliminated cycling accidents."\nWhat is the main problem?',
          options: ['Too strong', 'Wrong speaker', 'Correct inference'],
          answer: 'Too strong',
          explanation:
            '"Eliminated" is much stronger than "changed little".',
        },
        {
          text:
            'Passage: "The article mentions that one architect preferred glass facades, though the writer later argues for mixed materials."\nWrong option: "The writer strongly supports glass facades."\nWhat is the main problem?',
          options: ['Wrong speaker', 'Too broad', 'Correct inference'],
          answer: 'Wrong speaker',
          explanation:
            'The preference belongs to one architect, not the writer.',
        },
      ],
    },
    {
      title: 'Pick the Best Supported Inference',
      type: 'select',
      instructions: 'Choose the option most reasonably supported by the passage.',
      questions: [
        {
          text:
            'Passage: "After training was shortened, new staff made more reporting errors in their first month."\nWhich inference is best supported?',
          options: [
            'Shorter training may have reduced preparation quality.',
            'All training is ineffective.',
            'New staff disliked their managers.',
          ],
          answer: 'Shorter training may have reduced preparation quality.',
          explanation:
            'This is a cautious inference directly linked to the reported error increase.',
        },
        {
          text:
            'Passage: "Visitors spent longer in galleries where seating was available."\nWhich inference is best supported?',
          options: [
            'Comfort may encourage longer visits.',
            'Museums should remove all standing exhibits.',
            'Visitors only care about furniture.',
          ],
          answer: 'Comfort may encourage longer visits.',
          explanation:
            'The passage supports a cautious relationship, not an extreme conclusion.',
        },
      ],
    },
    {
      title: 'Explain Your Elimination',
      type: 'writing',
      instructions: 'Explain why one wrong option is tempting but still wrong.',
      questions: [
        {
          text:
            'Passage: "The company\'s profits rose, but only after two difficult years of losses."\nWrong option: "The company became profitable immediately after the new manager arrived."\nWhy might this option tempt someone, and why is it wrong?',
          example:
            'It is tempting because the passage mentions profits rising, but it is wrong because the option adds "immediately", which the text contradicts by describing two difficult years first.',
          tip:
            'Good elimination explanations focus on the single word or phrase that breaks the match.',
        },
      ],
    },
  ],
  tips: [
    'Underline certainty words in each option: always, may, likely, only, most.',
    'If two options seem close, compare their scope and strength carefully.',
    'Use the passage to eliminate before you choose.',
    'For inference questions, choose the most cautious answer that the evidence supports.',
  ],
  dailyPractice: [
    'Take one reading paragraph and write one correct option plus two distractors.',
    'Practice labeling distractors as too strong, partial truth, or wrong focus.',
    'Review advanced multiple-choice questions by asking why each wrong option fails.',
    'Summarize one paragraph, then write the safest inference it supports.',
  ],
  backPath: '/exams/english/#reading',
  backLabel: 'Back to Reading',
  aiContext:
    'Multiple Choice Advanced: eliminating distractors, comparing close options, and choosing the answer best supported by the passage.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...multipleChoiceAdvancedData,
      title: multipleChoiceAdvancedData.title + ' (Foundations)',
      overview: { ...multipleChoiceAdvancedData.overview, duration: '15-20 minutes' },
      sections: multipleChoiceAdvancedData.sections.slice(0, 2),
      exercises: [
        {
          ...multipleChoiceAdvancedData.exercises[0],
          title: 'Eliminate the Distractor (Basic)',
          questions: multipleChoiceAdvancedData.exercises[0].questions.slice(0, 1),
        },
        {
          ...multipleChoiceAdvancedData.exercises[1],
          title: 'Why the Wrong Option Fails (Basic)',
          questions: multipleChoiceAdvancedData.exercises[1].questions.slice(0, 1),
        },
      ],
    };
  }
  if (difficulty >= 4) {
    return {
      ...multipleChoiceAdvancedData,
      title: multipleChoiceAdvancedData.title + ' (Advanced)',
      overview: { ...multipleChoiceAdvancedData.overview, duration: '30-35 minutes' },
      sections: [
        ...multipleChoiceAdvancedData.sections,
        {
          title: 'Advanced Near-Match Options',
          content:
            '<p>The hardest items contain two options that both fit part of the passage. Choose the option that fits the <strong>whole claim</strong> with the right degree of certainty.</p>',
          points: [
            'Reject options that sneak in universal language.',
            'Watch whether the option reflects the author, a source, or a side comment.',
            'Look for the sentence that settles the comparison between the final two options.',
          ],
        },
      ],
      exercises: [
        ...multipleChoiceAdvancedData.exercises.map((ex) => ({
          ...ex,
          title: ex.title + ' (Advanced)',
        })),
        {
          title: 'Final-Two Comparison',
          type: 'select',
          instructions: 'Choose the stronger of two close options.',
          questions: [
            {
              text:
                'Passage: "The review suggests that the reform has improved access in urban areas, but its effect in rural regions remains limited."\nBest answer:',
              options: [
                'The reform has improved access everywhere.',
                'The reform has had mixed results depending on location.',
              ],
              answer: 'The reform has had mixed results depending on location.',
              explanation:
                'The passage contrasts urban improvement with rural limitation, so the effect is mixed, not universal.',
            },
          ],
        },
      ],
      tips: [
        ...(multipleChoiceAdvancedData.tips || []),
        'At Band 8+, compare the last two options sentence by sentence against the passage.',
        'The best answer is often the more precise and less exaggerated one.',
      ],
    };
  }
  return multipleChoiceAdvancedData;
}
